import { useTheme } from '@emotion/react'
import { ArrowForward } from '@mui/icons-material'
import { Card, Stack, Typography } from '@mui/material'
import { RosterListProps } from 'client/pages/RosterSearches/ClientRosterCardPreview'
import { FC } from 'react'
import { Link } from 'react-router-dom'
import RosterTypePill from 'shared/components/RosterTypePill'
import SharedWithAvatars from 'shared/components/SharedWithAvatars'
import useIsMobile from 'shared/hooks/useIsMobile'
import { getPxAsRem } from 'shared/utils'

export const noWrap = {
    flexDirection: 'column',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    flexWrap: 'nowrap',
} as const

const RosterCard: FC<RosterListProps> = ({
    rosterName,
    rosterId: id,
    details,
    rosterType,
}) => {
    const theme = useTheme()
    const isDesktop = !useIsMobile()
    return (
        <Card
            className="roster-card"
            component={Link}
            to={`${id}`}
            sx={{
                textDecoration: 'none',
                padding: isDesktop ? getPxAsRem(16, 24) : getPxAsRem(8),
                display: 'flex',
                flexDirection: 'row',
                rounded: '8px',

                '&:focus': {
                    outline: `2px solid ${theme.palette.primary.main}`,
                },
            }}
        >
            <Stack
                direction="row"
                gap={getPxAsRem(8)}
                sx={{
                    flexGrow: 1,
                    alignItems: isDesktop ? 'flex-start' : 'center',
                    justifyContent: 'space-between',
                }}
            >
                <Typography
                    variant={isDesktop ? 'h5' : 'h6'}
                    sx={{
                        flexGrow: 0,
                        flexShrink: 0,
                        width: getPxAsRem(300),
                        margin: 'auto 0',
                        ...noWrap,
                    }}
                >
                    {rosterName}
                </Typography>

                <Stack
                    alignItems={'flex-start'}
                    alignContent={'center'}
                    sx={{
                        width: getPxAsRem(100),
                        ...noWrap,
                    }}
                >
                    {isDesktop && (
                        <Typography
                            variant="overline"
                            sx={noWrap}
                            color={theme.palette.other.secondary_light}
                        >
                            Type
                        </Typography>
                    )}
                    <RosterTypePill
                        rosterType={rosterType}
                        sx={{ width: 'fit-content' }}
                    />
                </Stack>

                {isDesktop && (
                    <>
                        <Stack sx={{ width: getPxAsRem(200), ...noWrap }}>
                            <Typography
                                sx={noWrap}
                                variant="overline"
                                color={theme.palette.other.secondary_light}
                            >
                                Company
                            </Typography>
                            <Typography variant="body1" sx={noWrap}>
                                {details.company ?? '-'}
                            </Typography>
                        </Stack>
                        <Stack sx={{ width: getPxAsRem(200), ...noWrap }}>
                            <Typography
                                variant="overline"
                                color={theme.palette.other.secondary_light}
                                sx={noWrap}
                            >
                                Industries
                            </Typography>
                            <Typography variant="body1" sx={noWrap}>
                                {details.industries?.join(', ') ?? '-'}
                            </Typography>
                        </Stack>
                        <Stack sx={{ width: getPxAsRem(200), ...noWrap }}>
                            <Typography
                                variant="overline"
                                sx={noWrap}
                                color={theme.palette.other.secondary_light}
                            >
                                Sectors
                            </Typography>
                            <Typography variant="body1" sx={noWrap}>
                                {details.sectors?.join(', ') ?? '-'}
                            </Typography>
                        </Stack>
                        <Stack sx={{ width: getPxAsRem(200), ...noWrap }}>
                            <Typography
                                variant="overline"
                                sx={noWrap}
                                color={theme.palette.other.secondary_light}
                            >
                                Locations
                            </Typography>
                            <Typography variant="body1" sx={noWrap}>
                                {details.officeLocations?.join(', ') ?? '-'}
                            </Typography>
                        </Stack>
                    </>
                )}

                {isDesktop && (
                    <Stack sx={{ width: getPxAsRem(150), ...noWrap }}>
                        <SharedWithAvatars
                            label={'Internal Users'}
                            users={details.sharedRecruiters}
                        />
                    </Stack>
                )}
                {isDesktop && (
                    <>
                        <Stack sx={{ width: getPxAsRem(100), ...noWrap }}>
                            <SharedWithAvatars
                                users={details.sharedClients}
                                label={'Shared With'}
                            />
                        </Stack>
                        <ArrowForward
                            sx={{
                                fill: theme.palette.greyscale.grey_600,
                                margin: 'auto 0',
                            }}
                        />
                    </>
                )}
            </Stack>
        </Card>
    )
}

export default RosterCard
